import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { eventsApi, Event, ResponseSummary } from '../services/api';
import { format, parseISO } from 'date-fns';
import {
    ArrowLeft,
    Send,
    X,
    Clock,
    Users,
    Calendar,
    Mail,
    Check,
    Edit,
    UserPlus,
    CalendarCheck
} from 'lucide-react';

const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = minutes / 60;
    if (hours === 1) return '1 hour';
    if (Number.isInteger(hours)) return `${hours} hours`;
    return `${hours} hours`;
};

export function EventDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [event, setEvent] = useState<Event | null>(null);
    const [responses, setResponses] = useState<ResponseSummary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [newRecipientEmail, setNewRecipientEmail] = useState('');
    const [isAddingRecipient, setIsAddingRecipient] = useState(false);

    const [isConverting, setIsConverting] = useState(false);

    useEffect(() => {
        if (id) {
            loadEvent(id);
        }
    }, [id]);

    const loadEvent = async (eventId: string) => {
        try {
            setIsLoading(true);
            const [eventData, responseData] = await Promise.all([
                eventsApi.get(eventId),
                eventsApi.getResponses(eventId).catch(() => null)
            ]);
            setEvent(eventData);
            setResponses(responseData);
        } catch (err: any) {
            setError(err.message || 'Failed to load event');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSend = async () => {
        if (!id) return;
        try {
            await eventsApi.send(id);
            loadEvent(id);
        } catch (err: any) {
            alert(err.message || 'Failed to send invitations');
        }
    };

    const handleCancel = async () => {
        if (!id) return;
        if (!confirm('Are you sure you want to cancel this event? Recipients will be notified.')) return;

        try {
            await eventsApi.cancel(id);
            loadEvent(id);
        } catch (err: any) {
            alert(err.message || 'Failed to cancel event');
        }
    };

    const getStatusColor = (status: Event['status']) => {
        switch (status) {
            case 'draft': return 'var(--color-gray-500)';
            case 'sent': return 'var(--color-primary)';
            case 'closed': return 'var(--color-success)';
            case 'canceled': return 'var(--color-error)';
        }
    };

    const handleAddRecipient = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !newRecipientEmail.trim()) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newRecipientEmail.trim())) {
            alert('Please enter a valid email address');
            return;
        }

        setIsAddingRecipient(true);
        try {
            await eventsApi.addRecipient(id, newRecipientEmail.trim());
            setNewRecipientEmail('');
            loadEvent(id);
        } catch (err: any) {
            alert(err.message || 'Failed to add recipient');
        } finally {
            setIsAddingRecipient(false);
        }
    };

    const handleConvertToMeeting = async (slotId: string, slotTime: string) => {
        if (!id) return;

        const confirmMsg = `Send calendar invites for ${slotTime} to all ${event?.recipient_count} recipients?`;
        if (!confirm(confirmMsg)) return;

        setIsConverting(true);
        try {
            const result = await eventsApi.convertToMeeting(id, slotId);

            if (result.meeting_type === 'teams' && result.teams_link) {
                alert(`Teams meeting created! ${result.sent_count} invites sent for ${result.meeting_time}\n\nJoin link: ${result.teams_link}`);
            } else {
                alert(`Calendar invites sent to ${result.sent_count} recipients for ${result.meeting_time}`);
            }

            navigate('/dashboard');
        } catch (err: any) {
            alert(err.message || 'Failed to send calendar invites');
        } finally {
            setIsConverting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="page container">
                <div className="alert alert-error">{error || 'Event not found'}</div>
                <Link to="/dashboard" className="btn btn-secondary">
                    <ArrowLeft size={18} /> Back to Dashboard
                </Link>
            </div>
        );
    }

    return (
        <div className="page container">
            <div style={{ marginBottom: 'var(--space-6)' }}>
                <Link
                    to="/dashboard"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: 'var(--color-gray-500)',
                        marginBottom: 'var(--space-3)'
                    }}
                >
                    <ArrowLeft size={16} /> Back to Dashboard
                </Link>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{
                            display: 'inline-block',
                            padding: 'var(--space-1) var(--space-3)',
                            background: getStatusColor(event.status),
                            color: 'white',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            {event.status.toUpperCase()}
                        </div>
                        <h1>{event.event_name}</h1>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        {event.status === 'draft' && (
                            <>
                                <Link to={`/events/${event.id}/edit`} className="btn btn-secondary">
                                    <Edit size={18} /> Edit
                                </Link>
                                <button onClick={handleSend} className="btn btn-primary">
                                    <Send size={18} /> Send Invitations
                                </button>
                            </>
                        )}
                        {event.status === 'sent' && (
                            <button onClick={handleCancel} className="btn btn-danger">
                                <X size={18} /> Cancel Event
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
                <div>
                    <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>Meeting Poll Details</h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Clock size={18} style={{ color: 'var(--color-gray-400)' }} />
                                <span>{formatDuration(event.duration_minutes)}</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Users size={18} style={{ color: 'var(--color-gray-400)' }} />
                                <span>{event.recipient_count} recipient{event.recipient_count !== 1 ? 's' : ''}</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Calendar size={18} style={{ color: 'var(--color-gray-400)' }} />
                                <span>Created {format(parseISO(event.created_at), 'MMM d, yyyy \'at\' h:mm a')}</span>
                            </div>

                            {event.sent_at && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <Mail size={18} style={{ color: 'var(--color-gray-400)' }} />
                                    <span>Sent {format(parseISO(event.sent_at), 'MMM d, yyyy \'at\' h:mm a')}</span>
                                </div>
                            )}
                        </div>

                        {event.description && (
                            <div style={{
                                marginTop: 'var(--space-4)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-gray-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-gray-200)'
                            }}>
                                <strong style={{ fontSize: 'var(--font-size-sm)', display: 'block', marginBottom: 'var(--space-2)' }}>Description:</strong>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>
                            </div>
                        )}

                        {event.comments && (
                            <div style={{
                                marginTop: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-gray-100)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <strong style={{ fontSize: 'var(--font-size-sm)', display: 'block', marginBottom: 'var(--space-2)' }}>Internal Notes:</strong>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{event.comments}</p>
                            </div>
                        )}

                        {event.custom_fields && event.custom_fields.length > 0 && (
                            <div style={{
                                marginTop: 'var(--space-4)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-primary-light)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-primary)'
                            }}>
                                <strong style={{ fontSize: 'var(--font-size-sm)', display: 'block', marginBottom: 'var(--space-2)' }}>Custom Fields:</strong>
                                <ul style={{ marginLeft: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
                                    {event.custom_fields.map((field, idx) => (
                                        <li key={idx} style={{ marginBottom: 'var(--space-1)' }}>
                                            {field.field_label} ({field.field_type}){field.is_required && ' *'}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="card">
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>
                            Time Slots ({event.time_slots?.length || 0})
                        </h3>

                        {event.time_slots && event.time_slots.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                {event.time_slots.map(slot => (
                                    <div
                                        key={slot.id}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: 'var(--space-3)',
                                            background: 'var(--color-gray-50)',
                                            borderRadius: 'var(--radius-sm)'
                                        }}
                                    >
                                        <span>
                                            {format(parseISO(slot.start_time_utc), 'EEE, MMM d \'at\' h:mm a')}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                            {slot.response_count !== undefined && event.status === 'sent' && (
                                                <span style={{
                                                    background: 'var(--color-primary-light)',
                                                    color: 'var(--color-primary-hover)',
                                                    padding: 'var(--space-1) var(--space-3)',
                                                    borderRadius: 'var(--radius-full)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 500
                                                }}>
                                                    {slot.response_count} vote{slot.response_count !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                            {event.status === 'sent' && (
                                                <button
                                                    onClick={() => handleConvertToMeeting(
                                                        slot.id,
                                                        format(parseISO(slot.start_time_utc), 'EEE, MMM d \'at\' h:mm a')
                                                    )}
                                                    disabled={isConverting}
                                                    className="btn btn-sm"
                                                    style={{
                                                        background: 'var(--color-success)',
                                                        color: 'white',
                                                        padding: '4px 12px',
                                                        fontSize: 'var(--font-size-xs)'
                                                    }}
                                                    title="Book this time and send calendar invites"
                                                >
                                                    <CalendarCheck size={14} style={{ marginRight: '4px' }} />
                                                    {isConverting ? 'Sending...' : 'Book'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: 'var(--color-gray-500)' }}>No time slots defined</p>
                        )}
                    </div>
                </div>

                <div>
                    <div className="card">
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>
                            Responses ({event.response_count} of {event.recipient_count})
                        </h3>

                        {event.status === 'draft' ? (
                            <p style={{ color: 'var(--color-gray-500)' }}>
                                Send invitations to start collecting responses
                            </p>
                        ) : responses?.recipient_breakdown && responses.recipient_breakdown.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {responses.recipient_breakdown.map((recipient, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: 'var(--space-3)',
                                            border: '1px solid var(--color-gray-200)',
                                            borderRadius: 'var(--radius-md)'
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            <strong>{recipient.recipient_email}</strong>
                                            {recipient.has_responded ? (
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    color: 'var(--color-success)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}>
                                                    <Check size={14} /> Responded
                                                </span>
                                            ) : (
                                                <span style={{
                                                    color: 'var(--color-gray-400)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}>
                                                    Pending
                                                </span>
                                            )}
                                        </div>

                                        {recipient.selected_times && (
                                            <div style={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-gray-500)'
                                            }}>
                                                Selected: {recipient.selected_times.split(',').length} time{recipient.selected_times.split(',').length !== 1 ? 's' : ''}
                                            </div>
                                        )}

                                        {recipient.custom_field_data && Object.keys(recipient.custom_field_data).length > 0 && (
                                            <div style={{
                                                marginTop: 'var(--space-3)',
                                                paddingTop: 'var(--space-3)',
                                                borderTop: '1px solid var(--color-gray-200)'
                                            }}>
                                                <strong style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-gray-600)',
                                                    display: 'block',
                                                    marginBottom: 'var(--space-2)'
                                                }}>
                                                    Custom Field Responses:
                                                </strong>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                                    {event.custom_fields?.map(field => {
                                                        const value = recipient.custom_field_data?.[field.field_name];
                                                        if (!value) return null;
                                                        
                                                        return (
                                                            <div key={field.field_name} style={{
                                                                fontSize: 'var(--font-size-sm)',
                                                                display: 'flex',
                                                                gap: 'var(--space-2)'
                                                            }}>
                                                                <span style={{ color: 'var(--color-gray-500)', minWidth: '120px' }}>
                                                                    {field.field_label}:
                                                                </span>
                                                                <span style={{ color: 'var(--color-gray-700)' }}>
                                                                    {Array.isArray(value) ? value.join(', ') : value}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: 'var(--color-gray-500)' }}>No responses yet</p>
                        )}
                    </div>

                    {event.status === 'sent' && (
                        <div className="card" style={{ marginTop: 'var(--space-4)' }}>
                            <h3 style={{ marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <UserPlus size={18} /> Add Recipient
                            </h3>
                            <form onSubmit={handleAddRecipient} style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <input
                                    type="email"
                                    className="input"
                                    placeholder="email@example.com"
                                    value={newRecipientEmail}
                                    onChange={(e) => setNewRecipientEmail(e.target.value)}
                                    disabled={isAddingRecipient}
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isAddingRecipient || !newRecipientEmail.trim()}
                                >
                                    {isAddingRecipient ? 'Adding...' : 'Add & Send'}
                                </button>
                            </form>
                            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', marginTop: 'var(--space-2)' }}>
                                New recipients will immediately receive an invitation email.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
